import Connections from '../../connections';
import {
  action, observable,
} from 'mobx';
import {
  getNotifications,
  resendNotification,
  sendNotification,
  sendGroupNotification,
  updateNotificationScheduled,
  updateNotificationBatches
} from '../../axios/notificationsRequests';
import {
  uploadFileURL,
} from '../../axios/requests';
import {
  convertCountriesToISOCodeString,
} from '../../constants/countries';
import {
  uploadFile,
} from '../../helperFunctions/uploadService';
import {
  ICountry,
} from '../storeInterfaces/authInterface';
import {
  INotification, Notification, NotificationSelection,
} from '../storeInterfaces/notificationInterface';
import { RequestStatus } from 'mobx-store';
import { NotificationBatch } from 'screens/sendNotificationScreen/NotificationBatches/types';

export class NotificationStore implements INotification {
  @observable notificationsList: Notification[] = [];

  @observable selectedNotification = new Notification();

  @observable loading = false;

  @observable addLoading = false;

  @observable getNotificationRequestStatus: RequestStatus = RequestStatus.IDLE;

  @action
  public async getNotifications(countries: ICountry[]): Promise<void> {
    try {
      this.loading = true;
      this.getNotificationRequestStatus = RequestStatus.LOADING;
      const availableCountries = convertCountriesToISOCodeString(countries);

      const response = await Connections.wadiDeglaAxios.httpGet(
        getNotifications,
        {
          params: {
            availableCountries,
            selection: NotificationSelection.ALL,
          },
        }
      );
      this.notificationsList = this.restructureNotificationsList(response.data);
      this.getNotificationRequestStatus = RequestStatus.SUCCESS;
    } catch (error) {
      this.getNotificationRequestStatus = RequestStatus.FAILED;
      console.log(error);
    } finally {
      this.loading = false;
    }
  }

  public async resendNotification(id: number): Promise<void> {
    try {
      this.loading = true;
      const response = await Connections.wadiDeglaAxios.httpGet(
        resendNotification(id)
      );
    } catch (error) {
      console.log(error);
    } finally {
      this.loading = false;
    }
  }

  public async toggleNotificationBatchesStatus(
    id: number,
    canceled: boolean
  ): Promise<void> {
    try {
      this.loading = true;
      const response = await Connections.wadiDeglaAxios.httpPut(
        updateNotificationBatches(id),
        {
          canceled,
        }
      );
    } catch (error) {
      console.log(error);
      return Promise.reject(error);
    } finally {
      this.loading = false;
    }
  }

  public async toggleNotificationScheduledStatus(
    id: number,
    canceled: boolean
  ): Promise<void> {
    try {
      this.loading = true;
      const response = await Connections.wadiDeglaAxios.httpPut(
        updateNotificationScheduled(id),
        {
          canceled,
        }
      );
    } catch (error) {
      console.log(error);
      return Promise.reject(error);
    } finally {
      this.loading = false;
    }
  }

  sendRegularNotification = async (
    titleEn: string,
    bodyEn: string,
    titleAr: string,
    bodyAr: string,
    hideTitle: boolean,
    audience: "members" | "nonmembers" | "all",
    imageEn: File[],
    imageAr: File[],
    link: string,
    type: string,
    navigationItemType: string,
    navigationItemId: number,
    countries: ICountry[],
    notificationChannel: string,
    batches: NotificationBatch[],
    sendTime: string,
    nativeModuleName: string,
    nativeModuleMethodName: string
  ) => {
    this.addLoading = true;
    const notificationImageUrlEn = await this.uploadSingleImage(imageEn);
    const notificationImageUrlAr = await this.uploadSingleImage(imageAr);
    const data = {
      titleEn,
      bodyEn,
      titleAr,
      bodyAr,
      showTitle: !hideTitle,
      imageURLEn: notificationImageUrlEn,
      imageURLAr: notificationImageUrlAr,
      audience,
      link: link || null,
      type,
      entityType: navigationItemType,
      entityId: navigationItemId !== -1 ? navigationItemId : null,
      availableCountries: countries.map((country: ICountry) => country.isoCode),
      channelId: notificationChannel,
      batches: batches.map((b) => {
        delete b.valid;
        return b;
      }),
      sendTime: !batches.length ? sendTime : undefined,
      ...(nativeModuleMethodName && { nativeModuleMethodName }),
      ...(nativeModuleName && { nativeModuleName }),
    };
    await Connections.wadiDeglaAxios.httpPost(sendNotification, data);
  };

  sendGroupNotification = async (
    titleEn: string,
    bodyEn: string,
    titleAr: string,
    bodyAr: string,
    hideTitle: boolean,
    file: File,
    audience: "members" | "nonmembers" | "all" | "group",
    imageEn: File[],
    imageAr: File[],
    link: string,
    type: string,
    navigationItemType: string,
    navigationItemId: number,
    countries: ICountry[],
    notificationChannel: string,
    nativeModuleName: string,
    nativeModuleMethodName: string
  ) => {
    this.addLoading = true;
    const notificationImageUrlEn = await this.uploadSingleImage(imageEn);
    const notificationImageUrlAr = await this.uploadSingleImage(imageAr);
    const payload = {
      titleEn,
      bodyEn,
      titleAr,
      bodyAr,
      showTitle: !hideTitle,
      imageURLAr: notificationImageUrlAr,
      imageURLEn: notificationImageUrlEn,
      audience,
      link: link || null,
      type,
      entityType: navigationItemType,
      entityId: navigationItemId !== -1 ? navigationItemId : null,
      availableCountries: countries.map((c: ICountry) => c.isoCode),
      channelId: notificationChannel,
      nativeModuleMethodName,
      nativeModuleName,
    };

    const formData = new FormData();

    formData.append("file", file); // actual file
    formData.append("data", JSON.stringify(payload)); // JSON as string

    await Connections.wadiDeglaAxios.httpPost(
      sendGroupNotification,
      formData
      // 🚫 do NOT set Content-Type manually
    );
  };

  public async sendNewNotification(
    titleEn: string,
    bodyEn: string,
    titleAr: string,
    bodyAr: string,
    hideTitle: boolean,
    audience: "members" | "nonmembers" | "all" | "group",
    imageEn: File[],
    imageAr: File[],
    file: File,
    link: string,
    type: string,
    navigationItemType: string,
    navigationItemId: number,
    countries: ICountry[],
    notificationChannel: string,
    batches: NotificationBatch[],
    sendTime: string,
    nativeModuleMethodName: string,
    nativeModuleName: string
  ): Promise<void> {
    return new Promise<void>(async (res, rej) => {
      try {
        if (audience === "group") {
          await this.sendGroupNotification(
            titleEn,
            bodyEn,
            titleAr,
            bodyAr,
            hideTitle,
            file,
            audience,
            imageEn,
            imageAr,
            link,
            type,
            navigationItemType,
            navigationItemId,
            countries,
            notificationChannel,
            nativeModuleName,
            nativeModuleMethodName
          );
        } else {
          await this.sendRegularNotification(
            titleEn,
            bodyEn,
            titleAr,
            bodyAr,
            hideTitle,
            audience,
            imageEn,
            imageAr,
            link,
            type,
            navigationItemType,
            navigationItemId,
            countries,
            notificationChannel,
            batches,
            sendTime,
            nativeModuleName,
            nativeModuleMethodName
          );
        }
        await this.getNotifications(countries);
        res();
      } catch (error) {
        console.log(error);
        rej(error);
      } finally {
        this.addLoading = false;
      }
    });
  }

  private restructureNotificationsList(notifications: any[]): any[] {
    const structuredNotificationsList = [];
    for (const notification of notifications) {
      const newNotification = new Notification();
      newNotification.setId(notification.notificationId);
      newNotification.setBody(notification.body);
      newNotification.setAudience(notification.audience);
      newNotification.setTitle(notification.title);
      newNotification.setCreatedAt(notification.createdAt);
      newNotification.setType(notification.type);
      newNotification.setScheduledSendTime(notification.sendTime);
      newNotification.setIsBatches(notification.isBatches);
      newNotification.setIsBatchesCanceled(notification.isBatchesCanceled);
      newNotification.setIsScheduledCanceled(notification.isScheduledCanceled);
      newNotification.setNativeScreenName(notification.nativeModuleName);
      newNotification.setNativeMethodName(notification.nativeModuleMethodName);
      if (notification.link) {
        newNotification.setLink(notification.link);
      }
      structuredNotificationsList.push(newNotification);
    }
    return structuredNotificationsList;
  }

  private uploadSingleImage(images: any[]) {
    const promises = [];
    const allImages: any[] = [];
    for (const image of images) {
      if (image.preview) {
        promises.push(
          uploadFile(uploadFileURL, image).then((res: any) => {
            allImages.push(res.data.downloadURL);
          })
        );
      } else {
        allImages.push(image.imageUrl);
      }
    }
    return Promise.all(promises)
      .then(() => {
        console.log("done");
        return allImages.length ? allImages[0] : null;
      })
      .catch((error) => {
        console.log("error");
        return allImages.length ? allImages[0] : null;
      });
  }
}
